import {
  IHeavyDutyMachinery,
  IHeavyDutyMachineryFilters,
  ICreateHeavyDutyMachineryRequest,
  IUpdateHeavyDutyMachineryRequest,
  ITakeOutOfServiceResponse,
} from '../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { IHeavyDutyMachinerySearchFilters, IHeavyDutyMachinerySearchResponse } from '../interfaces/heavyDutyMachinery/ISearch';
import { IAvailableMachineryAccessory } from '../interfaces/heavyDutyMachinery/IMachineryAccessory';
import { IAvailableHostInsurancePolicy } from '../interfaces/heavyDutyMachinery/IHostInsurancePolicy';
import { IAvailableService, INearbyOperator } from '../interfaces/heavyDutyMachinery/IService';
import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

// HeavyDutyMachineryApi is a separate host from AuthorizationApi (now only
// used for non-auth reference data like GetAllCountries -- login/register/
// everything auth-related moved to HeavyDutyMachineryApi's own
// AuthController, see that project's Program.cs).
const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

export const getHeavyDutyMachineries = async (filters?: IHeavyDutyMachineryFilters): Promise<IHeavyDutyMachinery[]> => {
  const params: Record<string, string> = {};
  if (filters?.country) params.country = filters.country;
  if (filters?.city) params.city = filters.city;
  if (filters?.administrativeLevel) params.administrativeLevel = filters.administrativeLevel;
  if (filters?.category) params.category = filters.category;
  if (filters?.rentalMode) params.rentalMode = filters.rentalMode;
  if (filters?.verifiedOnly != null) params.verifiedOnly = String(filters.verifiedOnly);
  if (filters?.minPrice != null) params.minPrice = String(filters.minPrice);
  if (filters?.maxPrice != null) params.maxPrice = String(filters.maxPrice);
  if (filters?.hostUserId) params.hostUserId = filters.hostUserId;

  const response = await apiService<IHeavyDutyMachinery[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACES,
    method: HTTP_METHODS.GET,
    params,
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load heavyDutyMachinery spaces');
};

// Landing page's "Featured this week" shelf.
export const getFeaturedSpaces = async (take = 8): Promise<IHeavyDutyMachinery[]> => {
  const response = await apiService<IHeavyDutyMachinery[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACES_FEATURED,
    method: HTTP_METHODS.GET,
    params: { take },
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load featured spaces');
};

// Landing page's "Top rated" shelf.
export const getTopRatedSpaces = async (take = 8): Promise<IHeavyDutyMachinery[]> => {
  const response = await apiService<IHeavyDutyMachinery[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACES_TOP_RATED,
    method: HTTP_METHODS.GET,
    params: { take },
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load top rated spaces');
};

// Distinct Category values with at least one Active listing -- feeds the
// homepage search bar's "Tipo de maquinaria" picker (see
// HomeSearchCategoryScreen), which needs this before any search has run.
// No caching on either side: the backend query is live, so the picker is
// always current as of whenever it's opened.
export const getActiveCategories = async (): Promise<string[]> => {
  const response = await apiService<string[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACES_CATEGORIES,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load active categories');
};

// One comma-separated query param per array filter — mirrors
// HeavyDutyMachinerySearchRequest.SplitCsv on the backend and doubles as the
// shape reflected into the URL's query string for a shareable search.
export const searchHeavyDutyMachineries = async (filters: IHeavyDutyMachinerySearchFilters): Promise<IHeavyDutyMachinerySearchResponse> => {
  const params: Record<string, string> = {};
  const hasPoint = filters.lat != null && filters.lng != null;
  // city is an exact text match against the space's own City/Province/
  // Region/FormattedAddress (see HeavyDutyMachineryController.ApplyFilters) --
  // AND-ed together with the lat/lng+radius box when both are sent, which
  // silently narrowed results by whichever was stricter with no visible
  // explanation (e.g. a location picked 50km away in a different town
  // vanishing from the map while the radius slider still read 100km/"no
  // limit"). city is now only sent as a filter when there's no resolved
  // point to search by radius with instead -- radius becomes the one real,
  // visible mechanism for "how far", matching what FiltersModal's slider
  // already implies it controls.
  if (filters.city && !hasPoint) params.city = filters.city;
  if (filters.lat != null) params.lat = String(filters.lat);
  if (filters.lng != null) params.lng = String(filters.lng);
  if (filters.radiusKm != null) params.radiusKm = String(filters.radiusKm);
  if (filters.rentalMode) params.rentalMode = filters.rentalMode;
  if (filters.startDate) params.checkInDate = filters.startDate;
  if (filters.endDate) params.checkOutDate = filters.endDate;
  if (filters.availabilityMode) params.availabilityMode = filters.availabilityMode;
  if (filters.availabilityAt) params.availabilityAt = filters.availabilityAt;
  if (filters.durationMinutes != null) params.durationMinutes = String(filters.durationMinutes);
  if (filters.minPrice != null) params.minPrice = String(filters.minPrice);
  if (filters.maxPrice != null) params.maxPrice = String(filters.maxPrice);

  // All four or none -- the backend ignores a partial bounding box.
  if (filters.minLat != null && filters.maxLat != null && filters.minLng != null && filters.maxLng != null) {
    params.minLat = String(filters.minLat);
    params.maxLat = String(filters.maxLat);
    params.minLng = String(filters.minLng);
    params.maxLng = String(filters.maxLng);
  }

  const csv = (values?: string[]) => (values && values.length > 0 ? values.join(',') : undefined);
  const arrayFilters: [string, string[] | undefined][] = [
    ['category', filters.category],
    ['accessType', filters.accessType],
    ['brand', filters.brand],
  ];
  arrayFilters.forEach(([key, values]) => {
    const joined = csv(values);
    if (joined) params[key] = joined;
  });

  if (filters.verifiedBadge) params.verifiedBadge = 'true';

  if (filters.weightTonsMin != null) params.weightTonsMin = String(filters.weightTonsMin);
  if (filters.weightTonsMax != null) params.weightTonsMax = String(filters.weightTonsMax);
  if (filters.powerHpMin != null) params.powerHpMin = String(filters.powerHpMin);
  if (filters.powerHpMax != null) params.powerHpMax = String(filters.powerHpMax);
  if (filters.dryHireOnly) params.dryHireOnly = 'true';
  if (filters.wetHireOnly) params.wetHireOnly = 'true';
  if (filters.deliveryOnly) params.deliveryOnly = 'true';

  if (filters.page != null) params.page = String(filters.page);
  if (filters.pageSize != null) params.pageSize = String(filters.pageSize);
  if (filters.cursor) params.cursor = filters.cursor;

  const response = await apiService<IHeavyDutyMachinerySearchResponse>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACES_SEARCH,
    method: HTTP_METHODS.GET,
    params,
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to search heavyDutyMachinery spaces');
};

export const getMyHeavyDutyMachineries = async (): Promise<IHeavyDutyMachinery[]> => {
  const response = await apiService<IHeavyDutyMachinery[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_MY_SPACES,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load your heavyDutyMachinery spaces');
};

export const getHeavyDutyMachineryById = async (id: string): Promise<IHeavyDutyMachinery | undefined> => {
  const response = await apiService<IHeavyDutyMachinery>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACE_BY_ID(id),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  return response.success ? response.data : undefined;
};

export const createHeavyDutyMachinery = async (request: ICreateHeavyDutyMachineryRequest): Promise<IHeavyDutyMachinery> => {
  const response = await apiService<IHeavyDutyMachinery>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACES,
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to create heavyDutyMachinery space');
};

export const updateHeavyDutyMachinery = async (
  id: string,
  request: IUpdateHeavyDutyMachineryRequest
): Promise<IHeavyDutyMachinery> => {
  const response = await apiService<IHeavyDutyMachinery>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACE_BY_ID(id),
    method: HTTP_METHODS.PUT,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update heavyDutyMachinery space');
};

// Self-serve Draft/PendingImprovement -> Active — stands in for the real
// host-verification flow, which doesn't exist yet (see HeavyDutyMachineryController.PublishSpace).
export const publishHeavyDutyMachinery = async (id: string): Promise<IHeavyDutyMachinery> => {
  const response = await apiService<IHeavyDutyMachinery>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACE_PUBLISH(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to publish heavyDutyMachinery space');
};

/**
 * The host's handbrake: this machine came back broken, take it off the
 * market now.
 *
 * Returns the bookings that run into the window ALONGSIDE the updated
 * machine, and the caller must show them. Parking the machine only stops
 * NEW bookings -- the confirmed one on Monday is still there, and it is the
 * one that costs money. Nothing is cancelled automatically: that is a
 * separate decision with a separate cost (see the backend's
 * TakeMachineryOutOfServiceResponse).
 *
 * `untilUtc` is optional and worth passing whenever the host has any idea:
 * with a date the machine returns to the market by itself, and the person
 * who would otherwise have to remember is the one lying under it.
 */
export const takeMachineryOutOfService = async (
  id: string,
  reason: string,
  untilUtc?: string
): Promise<ITakeOutOfServiceResponse> => {
  const response = await apiService<ITakeOutOfServiceResponse>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACE_OUT_OF_SERVICE(id),
    method: HTTP_METHODS.POST,
    body: { reason, untilUtc },
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to take the machine out of service');
};

/** Repaired early, or parked by mistake. */
export const putMachineryBackInService = async (id: string): Promise<IHeavyDutyMachinery> => {
  const response = await apiService<IHeavyDutyMachinery>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACE_BACK_IN_SERVICE(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to put the machine back in service');
};

export interface IAvailableOperator {
  id: string;
  fullName: string;
  hourlyRate: number;
  /** True unless startAt/endAt were passed and this operator already has another overlapping Booking -- still present in the list either way (see MachineryController.GetAvailableOperators's own doc comment on why this is display-only, never dropped), so a false here means "show disabled, with availableFromDate below", not "hide". Always true when startAt/endAt were omitted. */
  isAvailableForRequestedDates: boolean;
  /** Only set when isAvailableForRequestedDates is false -- when this operator opens back up. */
  availableFromDate?: string;
}

/** Verified+active staff Operators of this space's host, qualified for this space's own Category -- see MachineryController.GetAvailableOperators. Empty (not an error) when the host has none that qualify. startAt/endAt (the same naive space-local strings AvailabilityCalendar/CreateBooking use) are optional -- when omitted every operator comes back with isAvailableForRequestedDates=true (nothing to check against yet); passing both still returns every qualified operator, but one with another overlapping Booking comes back isAvailableForRequestedDates=false instead of being dropped. CreateBooking's own re-check is still what actually rejects an attempt to force-select one of those -- this list is display-only. */
export const getAvailableOperators = async (spaceId: string, startAt?: string, endAt?: string): Promise<IAvailableOperator[]> => {
  const response = await apiService<IAvailableOperator[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACE_AVAILABLE_OPERATORS(spaceId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params: startAt && endAt ? { startAt, endAt } : undefined,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the available operators');
};

/** This space's already-linked, IsActive MachineryAccessories -- see MachineryController.GetAvailableAccessories. Empty (not an error) when none are linked; the frontend's own job to skip the picker step in that case. */
/** startAt/endAt (the same naive space-local strings AvailabilityCalendar/CreateBooking use) are optional -- when omitted every accessory comes back with isAvailableForRequestedDates=true (nothing to check against yet); passing both still returns every linked+active accessory, but one with no free units for that window comes back isAvailableForRequestedDates=false with an availableFromDate instead of being dropped. CreateBooking's own re-check is still what actually rejects an attempt to force-select one of those -- this list is display-only. */
export const getAvailableAccessories = async (spaceId: string, startAt?: string, endAt?: string): Promise<IAvailableMachineryAccessory[]> => {
  const response = await apiService<IAvailableMachineryAccessory[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACE_AVAILABLE_ACCESSORIES(spaceId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params: startAt && endAt ? { startAt, endAt } : undefined,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the available accessories');
};

/**
 * Fase 13: Services attachable to a booking on THIS specific machine (any
 * provider's, not just this host's own staff) -- see
 * MachineryController.GetAvailableServices. Already filtered server-side
 * for AttachableToMachineryBooking + ServiceMachineryCompatibility, never
 * re-derive either here. Hidden behind ServicesEnabled like every other
 * Service endpoint -- a bare 404 (no message body) means the flag is off,
 * same convention ServiceService's own callers already use; the caller's
 * job to treat that the same as "empty" and simply skip this picker step.
 * startAt/endAt optional, same naive space-local strings as
 * getAvailableOperators/getAvailableAccessories.
 */
export const getAvailableServices = async (spaceId: string, startAt?: string, endAt?: string): Promise<IAvailableService[]> => {
  const response = await apiService<IAvailableService[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACE_AVAILABLE_SERVICES(spaceId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params: startAt && endAt ? { startAt, endAt } : undefined,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the available services');
};

/**
 * Operators working near a job, for the guest to hire THEMSELVES as a
 * booking of their own -- see MachineryController.GetNearbyOperators.
 * Never attached to the machinery booking; see INearbyOperator's own doc
 * comment for why that separation is the point.
 *
 * `lat`/`lng` are the JOB, not the machine: pass the delivery point when
 * the guest asked for delivery, and leave them out otherwise, in which case
 * the server measures from the machine's own location. Getting this wrong
 * silently measures from the wrong end of a transport.
 *
 * Empty is the normal answer in three different situations -- the host does
 * not allow outside operators, nobody compatible is within range, or the
 * Services feature is off entirely (a bare 404, same convention as
 * getAvailableServices). All three mean the same thing to a caller: render
 * nothing.
 */
export const getNearbyOperators = async (
  spaceId: string,
  options?: { startAt?: string; endAt?: string; lat?: number; lng?: number; radiusKm?: number }
): Promise<INearbyOperator[]> => {
  const { startAt, endAt, lat, lng, radiusKm } = options ?? {};
  const response = await apiService<INearbyOperator[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACE_NEARBY_OPERATORS(spaceId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params: {
      ...(startAt && endAt ? { startAt, endAt } : {}),
      ...(lat != null && lng != null ? { lat: String(lat), lng: String(lng) } : {}),
      ...(radiusKm != null ? { radiusKm: String(radiusKm) } : {}),
    },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the nearby operators');
};

/** This space's active linked HostInsurancePolicy, if any -- see MachineryController.GetAvailableInsurancePolicy. Null (not an error) when this machine offers no host-provided insurance; the frontend's own job to skip the third checkout option in that case. */
export const getAvailableInsurancePolicy = async (spaceId: string): Promise<IAvailableHostInsurancePolicy | null> => {
  const response = await apiService<IAvailableHostInsurancePolicy | null>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACE_AVAILABLE_INSURANCE_POLICY(spaceId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the available insurance policy');
};

export const deleteHeavyDutyMachinery = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SPACE_BY_ID(id),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to delete heavyDutyMachinery space');
};
